import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useCurrentUser } from "./use-current-user";

interface UserOrganization {
  id: string;
  name: string;
  relation?: 'member' | 'contractor' | 'owner';
  contractor_role?: 'contractor' | 'watcher' | 'business_owner' | null;
}

/**
 * Hook to fetch all organizations where the current user is a contractor
 */
export function useContractorOrganizations() {
  const { user } = useCurrentUser();
  const userId = (user as any)?.id || null;
  const { data, isLoading, error } = useQuery<{
    success: boolean;
    data: UserOrganization[];
  }>({
    queryKey: ["/api/orgs/my-organizations", userId],
    queryFn: async () => {
      const response = await apiRequest("/api/orgs/my-organizations");
      return await response.json();
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 1,
    enabled: !!userId,
  });

  const { organizations, contractorOrganizations } = useMemo(() => {
    const organizationsRaw = data?.data || [];

    // Deduplicate organizations by ID (if user is both member and contractor, prefer member)
    const organizationsMap = new Map<string, UserOrganization>();
    for (const org of organizationsRaw) {
      const existing = organizationsMap.get(org.id);
      if (!existing) {
        organizationsMap.set(org.id, org);
      } else if (existing.relation === 'contractor' && org.relation === 'member') {
        // Prefer 'member' over 'contractor' if both exist
        organizationsMap.set(org.id, org);
      }
    }

    const dedupedOrgs = Array.from(organizationsMap.values());
    return {
      organizations: dedupedOrgs,
      contractorOrganizations: dedupedOrgs.filter(o => o.relation === 'contractor')
    };
  }, [data?.data]);

  const sanitizedOrganizations = userId ? organizations : [];
  const sanitizedContractorOrgs = userId ? contractorOrganizations : [];

  return {
    organizations: sanitizedOrganizations,
    contractorOrganizations: sanitizedContractorOrgs,
    isLoading: isLoading || !userId,
    error,
    hasMultipleOrgs: sanitizedOrganizations.length > 1,
  };
}
